import React, { useState } from 'react';
import { AlertTriangle, KeyRound, RefreshCw, X } from 'lucide-react';
import { certificateApi } from '../../services/iso20022Api';
import { downloadFile } from '../../utils/helpers';

const MIN_LENGTH = 12;

/**
 * Asks for a passphrase and downloads the certificate and its private key as a
 * PKCS#12 keystore, so the participant can sign certification traffic from
 * their own platform. The passphrase only protects the file; it is not stored.
 */
const ExportKeystoreModal = ({ certificate, onClose, onExported }) => {
  const [passphrase, setPassphrase] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [exportedAlias, setExportedAlias] = useState(null);

  const tooShort = passphrase.length < MIN_LENGTH;
  const mismatch = confirmation.length > 0 && confirmation !== passphrase;
  const canSubmit = !busy && !tooShort && confirmation === passphrase;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { blob, alias } = await certificateApi.exportKeystore(certificate.id, passphrase);
      downloadFile(blob, `${certificate.alias}.p12`, 'application/x-pkcs12');
      setExportedAlias(alias);
      setPassphrase('');
      setConfirmation('');
      onExported?.();
    } catch (err) {
      setError(err.response?.status === 403
        ? 'Only an administrator can export a private key.'
        : err.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label="Export private key">
      <div className="absolute inset-0 bg-gray-900/50" onClick={busy ? undefined : onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Export private key (.p12)</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="text-gray-400 hover:text-gray-600"
                  aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {exportedAlias ? (
          <div className="p-5 space-y-4 text-sm text-gray-700">
            <p>
              <span className="font-medium">{certificate.alias}.p12</span> has been downloaded.
              Load it in your signer with the passphrase you chose. The key entry is
              stored under the alias <code className="font-mono bg-gray-100 px-1 rounded">{exportedAlias}</code>.
            </p>
            <pre className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs overflow-x-auto">
{`java Iso20022BahMessageSigner sign --document pacs008.xml \\
     --cert ${certificate.alias}.pem --p12 ${certificate.alias}.p12 \\
     --alias ${exportedAlias} --pass '<passphrase>'`}
            </pre>
            {!certificate.isPrimary && (
              <p className="text-amber-700">
                This is not your active certificate. The certification switch checks signatures
                against the active one, so activate this certificate before you sign with it.
              </p>
            )}
            <div className="flex justify-end">
              <button onClick={onClose} className="btn-primary">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>
                This key is for sandbox certification only. Never use it in production.
                Anyone who has the file and the passphrase can sign as your institution in
                the sandbox, and every export is recorded in this certificate's audit log.
              </p>
            </div>

            <div>
              <label htmlFor="p12-passphrase" className="text-sm font-medium text-gray-700">
                Passphrase
              </label>
              <input id="p12-passphrase" type="password" autoComplete="new-password"
                     className="input mt-1 w-full" value={passphrase}
                     onChange={(e) => setPassphrase(e.target.value)} disabled={busy} autoFocus />
              <p className={`text-xs mt-1 ${passphrase && tooShort ? 'text-red-600' : 'text-gray-500'}`}>
                At least {MIN_LENGTH} characters. It protects the file and is not stored.
              </p>
            </div>

            <div>
              <label htmlFor="p12-confirm" className="text-sm font-medium text-gray-700">
                Confirm passphrase
              </label>
              <input id="p12-confirm" type="password" autoComplete="new-password"
                     className="input mt-1 w-full" value={confirmation}
                     onChange={(e) => setConfirmation(e.target.value)} disabled={busy} />
              {mismatch && <p className="text-xs mt-1 text-red-600">Passphrases do not match.</p>}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} disabled={busy} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={!canSubmit}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50">
                {busy && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>Export keystore</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ExportKeystoreModal;
